import React, { createContext, useContext } from "react"
import Link from "next/link"

import { FEATURE_FLAGS, useIsFeatureEnabled } from "@/feature-flags"
import {
  isPersistedTestSetId,
  useTestSetAutoAddSummaryTransport,
} from "@/test-set/transport"
import {
  Badge,
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@coval/ui"

import { useQueryParams } from "@/hooks/useQueryParams"

import { TestSetAutoAddPendingReview } from "./TestSetAutoAddPendingReview"
import { TestSetAutoAddRulePanel } from "./TestSetAutoAddRulePanel"

const EMPTY_LEARNED_CONVERSATIONS: Record<string, string> = {}
const LearnedConversationsContext = createContext(EMPTY_LEARNED_CONVERSATIONS)

export function LearnedTestCaseBadge({ testCaseId }: { testCaseId: string }) {
  const source = useContext(LearnedConversationsContext)[testCaseId]
  const queryParams = useQueryParams()
  if (!source) {
    return null
  }
  return (
    <div className="px-2 pb-3">
      <Link
        href={queryParams.getHref(
          `/conversations/results/${encodeURIComponent(source)}`,
        )}
        aria-label="Learned test case: view source conversation"
        title="View source conversation"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        className="inline-flex rounded-md focus-visible:outline-none"
      >
        <Badge variant="info" className="whitespace-nowrap text-xs">
          Learned
        </Badge>
      </Link>
    </div>
  )
}

interface TestSetAutoAddSectionProps {
  testSetId: string
  children: React.ReactNode
}

export const TestSetAutoAddSection: React.FC<TestSetAutoAddSectionProps> = (
  props,
) => {
  const enabled = useIsFeatureEnabled(FEATURE_FLAGS.AUTO_TEST_SET_GENERATION)
  return enabled && isPersistedTestSetId(props.testSetId) ? (
    <AutoAddTabs key={props.testSetId} {...props} />
  ) : (
    <>{props.children}</>
  )
}

const AutoAddTabs: React.FC<TestSetAutoAddSectionProps> = ({
  testSetId,
  children,
}) => {
  const summary = useTestSetAutoAddSummaryTransport(testSetId)
  return (
    <LearnedConversationsContext.Provider
      value={
        summary.data?.learned_from_conversations ?? EMPTY_LEARNED_CONVERSATIONS
      }
    >
      <Tabs
        defaultValue="test-cases"
        className="flex flex-1 min-h-0 flex-col"
        data-testid="test-set-auto-add-section"
      >
        {summary.error && (
          <div
            role="alert"
            className="flex items-center gap-3 px-6 py-2 text-sm text-coval-danger-text"
          >
            <span>Could not refresh auto-add status and learned sources.</span>
            <Button
              type="button"
              variant="secondary"
              onClick={() => void summary.refetch()}
            >
              Retry status
            </Button>
          </div>
        )}
        <div className="shrink-0 border-b border-coval-border px-6 overflow-x-auto">
          <TabsList
            variant="underline"
            className="gap-6 border-b-0"
            aria-label="Test set views"
          >
            <TabsTrigger value="test-cases" className="px-0 h-12">
              Test cases
            </TabsTrigger>
            <TabsTrigger value="pending" className="px-0 h-12">
              Pending test cases
              {summary.data && (
                <span className="ml-1 text-coval-text-muted font-normal">
                  {summary.data.pending_review_count}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="rules" className="px-0 h-12">
              Auto-add rules
            </TabsTrigger>
          </TabsList>
        </div>
        <TabsContent
          value="test-cases"
          forceMount
          className="mt-0 flex flex-1 min-h-0 flex-col data-[state=inactive]:hidden"
        >
          {children}
        </TabsContent>
        <TabsContent
          value="pending"
          forceMount
          className="mt-0 flex-1 min-h-0 overflow-auto p-6 data-[state=inactive]:hidden"
        >
          <div className="mx-auto w-full max-w-4xl space-y-6">
            <div className="space-y-1">
              <h2 className="text-lg font-semibold">Pending test cases</h2>
              <p className="text-sm text-coval-text-muted">
                Review new scenarios from monitored conversations. Accepted
                cases become available for simulations.
              </p>
            </div>
            <TestSetAutoAddPendingReview testSetId={testSetId} />
          </div>
        </TabsContent>
        <TabsContent
          value="rules"
          forceMount
          className="mt-0 flex-1 min-h-0 overflow-auto p-6 data-[state=inactive]:hidden"
        >
          <div className="mx-auto w-full max-w-3xl space-y-6">
            <div className="space-y-1">
              <h2 className="text-lg font-semibold">Auto-add rules</h2>
              <p className="text-sm text-coval-text-muted">
                Choose which conversations generate suggestions for this test
                set.
              </p>
            </div>
            <TestSetAutoAddRulePanel testSetId={testSetId} />
          </div>
        </TabsContent>
      </Tabs>
    </LearnedConversationsContext.Provider>
  )
}
